// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/MySearches.js
//
// Onglet « Mes recherches » : publier, modifier, renouveler, réactiver et clôturer ses annonces.
// Les droits (modifier, renouveler…) viennent du serveur ; l'interface propose, le serveur décide.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CAlert, CButton, CSpinner } from '@coreui/react'
import SearchCard from './SearchCard'
import SearchPager from './SearchPager'
import SearchFormModal from './SearchFormModal'
import CloseSearchModal from './CloseSearchModal'
import CareerStatusSuggestion from './CareerStatusSuggestion'
import MatchingConsentCard from './MatchingConsentCard'
import MatchesModal from './MatchesModal'
import ResponsesModal from './ResponsesModal'
import useSearchesConfig from './useSearchesConfig'
import {
  fetchMySearches,
  renewSearch,
  reactivateSearch,
  fetchMatchingConsent,
  setMatchingConsent,
  fetchSearchResponses,
} from '../../../utils/memberSearchesApi'
import { SEARCH_STATUSES, parseApiError, errorKeyFor } from '../../../utils/memberSearches'
import { loadPendingCounts } from '../../../utils/memberSearchResponses'
import './searches.scss'

const STATUS_FILTERS = ['', ...SEARCH_STATUSES]

/** @param {{memberId: (number|null)}} props  memberId : pour la proposition de statut de carrière. */
const MySearches = ({ memberId }) => {
  const { t } = useTranslation()
  const { config } = useSearchesConfig()
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState({ searches: [], pagination: null })
  const [activeCount, setActiveCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [notice, setNotice] = useState(null) // { color, text }
  const [suggestion, setSuggestion] = useState(null) // statut proposé, ou null
  const [formState, setFormState] = useState({ open: false, search: null })
  const [closing, setClosing] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [consent, setConsent] = useState(null) // null = pas encore chargé : rien n'est présenté comme activé
  const [matchesFor, setMatchesFor] = useState(null)
  const [responsesFor, setResponsesFor] = useState(null)
  const [pendingCounts, setPendingCounts] = useState(() => new Map())
  const seq = useRef(0)

  const load = useCallback(async (nextStatus, nextPage) => {
    const mine = ++seq.current
    setLoading(true)
    setFailed(false)
    try {
      const [list, actives] = await Promise.all([
        fetchMySearches({ status: nextStatus, page: nextPage }),
        fetchMySearches({ status: 'active', limit: 50 }),
      ])
      if (mine !== seq.current) return
      setData({ searches: list.searches || [], pagination: list.pagination || null })
      setActiveCount(
        actives.pagination ? actives.pagination.total : (actives.searches || []).length,
      )
    } catch (err) {
      if (mine !== seq.current) return
      setFailed(true)
      setData({ searches: [], pagination: null })
    } finally {
      if (mine === seq.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(status, page)
  }, [status, page, load])

  useEffect(() => {
    let alive = true
    fetchMatchingConsent()
      .then((res) => alive && setConsent(res.consent))
      .catch(() => alive && setConsent(null)) // échec : la carte reste masquée, aucune action proposée
    return () => {
      alive = false
    }
  }, [])

  // Compteur « en attente » des demandes de mise en relation actives affichées (le serveur n'en expose pas ; un échec
  // isolé n'affiche simplement aucun compteur).
  const refreshPendingCounts = useCallback(async (searches) => {
    const counts = await loadPendingCounts({
      searches,
      fetchPending: (id) => fetchSearchResponses(id, { status: 'pending', limit: 1 }),
    })
    setPendingCounts(counts)
  }, [])

  useEffect(() => {
    refreshPendingCounts(data.searches)
  }, [data.searches, refreshPendingCounts])

  const changeConsent = async (enabled) => {
    setNotice(null)
    try {
      const res = await setMatchingConsent(enabled)
      setConsent(res.consent)
      setNotice({
        color: 'success',
        text: t(enabled ? 'searches:matching.consent.enabled' : 'searches:matching.consent.disabled'),
      })
    } catch (err) {
      setNotice({ color: 'danger', text: t(`searches:${errorKeyFor(parseApiError(err).code)}`) })
    }
  }

  const reload = () => load(status, page)
  const maxActive = config ? config.maxActive : null
  const limitReached = maxActive != null && activeCount != null && activeCount >= maxActive

  const run = async (search, action, successKey) => {
    if (busyId) return
    setBusyId(search.id)
    setNotice(null)
    try {
      await action(search.id)
      setNotice({ color: 'success', text: t(successKey) })
      await reload()
    } catch (err) {
      setNotice({ color: 'danger', text: t(`searches:${errorKeyFor(parseApiError(err).code)}`) })
    } finally {
      setBusyId(null)
    }
  }

  const onSaved = ({ suggestCareerStatus, isEdit }) => {
    setNotice({
      color: 'success',
      text: t(isEdit ? 'searches:form.saved' : 'searches:form.published'),
    })
    setSuggestion(!isEdit && suggestCareerStatus && memberId ? suggestCareerStatus : null)
    if (status || page !== 1) {
      setStatus('')
      setPage(1)
    } else {
      reload()
    }
  }

  return (
    <div>
      <p className="ms-intro">
        {t('searches:intro.mine', { days: config ? config.durationDays : 90 })}
      </p>

      <div className="ms-toolbar">
        <CButton
          color="primary"
          size="sm"
          disabled={limitReached}
          onClick={() => setFormState({ open: true, search: null })}
        >
          {t('searches:actions.new')}
        </CButton>
        {maxActive != null && activeCount != null && (
          <span className="ms-quota">
            {t('searches:list.active_count', { count: activeCount, max: maxActive })}
          </span>
        )}
        <span className="ms-grow" />
        {STATUS_FILTERS.map((s) => (
          <CButton
            key={s || 'all'}
            size="sm"
            color={status === s ? 'primary' : 'light'}
            onClick={() => {
              setStatus(s)
              setPage(1)
            }}
          >
            {s ? t(`searches:status.${s}`) : t('searches:list.kind_all')}
          </CButton>
        ))}
      </div>

      {limitReached && <p className="ms-note">{t('searches:errors.active_limit')}</p>}

      <MatchingConsentCard consent={consent} onChange={changeConsent} />

      {notice && (
        <CAlert color={notice.color} dismissible onClose={() => setNotice(null)}>
          {notice.text}
        </CAlert>
      )}

      {suggestion && memberId && (
        <CareerStatusSuggestion
          memberId={memberId}
          status={suggestion}
          onDone={() => setSuggestion(null)}
        />
      )}

      {failed && <CAlert color="danger">{t('searches:list.loading_error')}</CAlert>}

      {loading ? (
        <div className="text-center py-5">
          <CSpinner />
        </div>
      ) : data.searches.length === 0 ? (
        !failed && <div className="ms-empty">{t('searches:list.empty_mine')}</div>
      ) : (
        data.searches.map((s) => (
          <SearchCard
            key={s.id}
            search={s}
            mode="mine"
            busy={busyId === s.id}
            onEdit={(item) => setFormState({ open: true, search: item })}
            onRenew={(item) => run(item, renewSearch, 'searches:feedback.renewed')}
            onReactivate={(item) => run(item, reactivateSearch, 'searches:feedback.reactivated')}
            onClose={(item) => setClosing(item)}
            onMatches={consent && consent.enabled ? (item) => setMatchesFor(item) : undefined}
            onResponses={(item) => setResponsesFor(item)}
            pendingCount={pendingCounts.get(s.id) || 0}
          />
        ))
      )}

      <SearchPager pagination={data.pagination} onPage={setPage} />

      <SearchFormModal
        visible={formState.open}
        search={formState.search}
        config={config}
        onClose={() => setFormState((prev) => ({ ...prev, open: false }))}
        onSaved={onSaved}
      />
      <MatchesModal search={matchesFor} onClose={() => setMatchesFor(null)} />
      <ResponsesModal
        search={responsesFor}
        onClose={() => setResponsesFor(null)}
        onChanged={() => refreshPendingCounts(data.searches)}
      />
      <CloseSearchModal
        search={closing}
        onClose={() => setClosing(null)}
        onClosed={() => {
          setNotice({ color: 'success', text: t('searches:close.done') })
          reload()
        }}
      />
    </div>
  )
}

export default MySearches
